import React from "react";
import { Container, Paper, Typography, Box, Button } from "@material-ui/core";
import { Link } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { nomeEmpresa } from "../../../../package.json";

const useStyles = makeStyles((theme) => ({
    root: {
        height: "100vh",
        backgroundColor: theme.palette.type === "light" ? theme.palette.primary.main : "#1c1c1c",
        overflowY: "auto",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),

        // Scrollbar vertical pill style with transparency
        "&::-webkit-scrollbar": {
            width: 8,
        },
        "&::-webkit-scrollbar-thumb": {
            background: "rgba(255, 255, 255, 0.35)",
            borderRadius: 100,
        },
        "&::-webkit-scrollbar-thumb:hover": {
            background: "rgba(255, 255, 255, 0.55)",
        },
        "&::-webkit-scrollbar-track": {
            background: "transparent",
        },
    },
    paper: {
        padding: theme.spacing(5),
        borderRadius: theme.neoUI?.floating.card?.borderRadius || 20,
        boxShadow: theme.neoUI?.floating.card?.boxShadow || "0 20px 60px -10px rgba(0, 0, 0, 0.15), 0 8px 24px -4px rgba(0, 0, 0, 0.1)",
        border: theme.neoUI?.floating.card?.border || "1px solid rgba(0, 0, 0, 0.04)",
        backgroundColor: "#ffffff",
        color: "#000000",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: theme.neoUI?.floating.card?.hoverShadow || "0 28px 70px -10px rgba(0, 0, 0, 0.18), 0 12px 30px -4px rgba(0, 0, 0, 0.12)",
        },
    },
    button: {
        backgroundColor: theme.palette.primary.main,
        color: "#ffffff",
        borderRadius: 10,
        padding: "10px 20px",
        fontWeight: 600,
        boxShadow: theme.neoUI?.floating.button?.boxShadow || "0 4px 12px rgba(20, 164, 92, 0.25)",
        transition: "all 0.3s ease-in-out",
        "&:hover": {
            backgroundColor: theme.palette.primary.dark,
            transform: "translateY(-2px)",
            boxShadow: theme.neoUI?.floating.button?.hoverShadow || "0 8px 20px rgba(20, 164, 92, 0.3)",
        },
    },
}));

const PrivacyPolicy = () => {
    const classes = useStyles();

    return (
        <div className={classes.root}>
            <Container maxWidth="md">
                <Paper className={classes.paper}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h4" component="h1" gutterBottom>
                            Política de Privacidade
                        </Typography>
                        <Button variant="contained" className={classes.button} component={Link} to="/login">
                            Voltar para Login
                        </Button>
                    </Box>

                    <Typography variant="body1" paragraph>
                        Última atualização: {new Date().toLocaleDateString()}
                    </Typography>

                    <Typography variant="body1" paragraph>
                        A sua privacidade é importante para o {nomeEmpresa}. Esta Política de Privacidade descreve como coletamos, usamos, armazenamos e protegemos suas informações pessoais ao utilizar nossa plataforma. Estamos comprometidos em cumprir a Lei Geral de Proteção de Dados (LGPD) e outras regulamentações aplicáveis.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                        1. Informações que Coletamos
                    </Typography>
                    <Typography variant="body1" paragraph>
                        Coletamos os seguintes tipos de informações:
                        <ul>
                            <li><strong>Informações da Conta:</strong> Nome, endereço de e-mail, número de telefone e dados da empresa necessários para criar e gerenciar sua conta.</li>
                            <li><strong>Dados de Uso:</strong> Registro de atividades, interações, e metadados gerados pelo uso da plataforma.</li>
                            <li><strong>Conteúdo das Comunicações:</strong> Mensagens processadas através da plataforma para fornecer o serviço de atendimento.</li>
                            <li><strong>Dados de Pagamento:</strong> Informações necessárias para processar pagamentos (processadas por gateways seguros).</li>
                        </ul>
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                        2. Como Usamos Suas Informações
                    </Typography>
                    <Typography variant="body1" paragraph>
                        Utilizamos suas informações para:
                        <ul>
                            <li>Fornecer, operar e manter nossos serviços.</li>
                            <li>Melhorar, personalizar e expandir nossa plataforma.</li>
                            <li>Entender e analisar como você usa nossos serviços.</li>
                            <li>Desenvolver novos produtos, serviços, recursos e funcionalidades.</li>
                            <li>Processar transações e enviar avisos relacionados.</li>
                            <li>Comunicar-se com você, diretamente ou através de um de nossos parceiros, para atendimento ao cliente, atualizações e fins de marketing.</li>
                        </ul>
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                        3. Compartilhamento de Informações
                    </Typography>
                    <Typography variant="body1" paragraph>
                        Não vendemos suas informações pessoais. Podemos compartilhar suas informações nas seguintes circunstâncias:
                        <ul>
                            <li><strong>Prestadores de Serviço:</strong> Com parceiros de confiança que nos ajudam a operar o serviço (ex: provedores de hospedagem, processadores de pagamento).</li>
                            <li><strong>Requisitos Legais:</strong> Quando exigido por lei ou para responder a processos legais válidos.</li>
                            <li><strong>Segurança:</strong> Para proteger nossos direitos, privacidade, segurança ou propriedade.</li>
                        </ul>
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                        4. Segurança dos Dados
                    </Typography>
                    <Typography variant="body1" paragraph>
                        Implementamos medidas de segurança técnicas e organizacionais adequadas para proteger suas informações pessoais contra acesso, alteração, divulgação ou destruição não autorizada. No entanto, nenhum método de transmissão pela Internet ou armazenamento eletrônico é 100% seguro.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                        5. Seus Direitos (LGPD)
                    </Typography>
                    <Typography variant="body1" paragraph>
                        Você tem o direito de:
                        <ul>
                            <li>Acessar seus dados pessoais.</li>
                            <li>Corrigir dados incompletos, inexatos ou desatualizados.</li>
                            <li>Solicitar a anonimização, bloqueio ou eliminação de dados desnecessários ou excessivos.</li>
                            <li>Solicitar a portabilidade dos dados a outro fornecedor de serviço.</li>
                            <li>Revogar seu consentimento a qualquer momento.</li>
                        </ul>
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                        6. Retenção de Dados
                    </Typography>
                    <Typography variant="body1" paragraph>
                        Reteremos suas informações pessoais apenas pelo tempo necessário para cumprir os propósitos estabelecidos nesta Política de Privacidade, a menos que um período de retenção mais longo seja exigido ou permitido por lei.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                        7. Alterações nesta Política
                    </Typography>
                    <Typography variant="body1" paragraph>
                        Podemos atualizar nossa Política de Privacidade periodicamente. Recomendamos que você revise esta página regularmente para verificar quaisquer alterações.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                        8. Contato
                    </Typography>
                    <Typography variant="body1" paragraph>
                        Se você tiver dúvidas sobre esta Política de Privacidade, entre em contato conosco.
                    </Typography>
                </Paper>
            </Container>
        </div>
    );
};

export default PrivacyPolicy;
